import React, { useCallback, useState } from 'react';

/**
 * Phase 3.1 — ToothNumberInput.
 *
 * First-class FDI tooth-number entry. Accepts:
 *   - Permanent FDI: ^[1-4][1-8]$  (e.g. 16, 26, 47)
 *   - Primary FDI:   ^[5-8][1-5]$  (e.g. 51, 65, 85)
 *   - Quadrant:      ^Q[1-4]$
 *   - Full-mouth:    ^FM$
 *
 * Mutual-exclusion rules:
 *   - A quadrant token displaces the individual FDI numbers it covers
 *     and vice versa.
 *   - `FM` displaces every other token; conversely any token addition
 *     drops `FM` first.
 *
 * Pure controlled component — `value: string[]`, `onChange(next)`.
 *
 * Props:
 *   value          — string[] of FDI / Q* / FM tokens
 *   onChange       — (next: string[]) => void
 *   dentitionMode  — used for context messages and validator hints
 *   isDark         — for dark-mode classes
 */
const PERMANENT_RE = /^[1-4][1-8]$/;
const PRIMARY_RE = /^[5-8][1-5]$/;
const QUADRANT_RE = /^Q[1-4]$/;
const FULL_MOUTH_RE = /^FM$/;

const QUADRANT_FDI_MAP = {
  Q1: ['11', '12', '13', '14', '15', '16', '17', '18', '51', '52', '53', '54', '55'],
  Q2: ['21', '22', '23', '24', '25', '26', '27', '28', '61', '62', '63', '64', '65'],
  Q3: ['31', '32', '33', '34', '35', '36', '37', '38', '71', '72', '73', '74', '75'],
  Q4: ['41', '42', '43', '44', '45', '46', '47', '48', '81', '82', '83', '84', '85'],
};

function quadrantOfFdi(fdi) {
  if (!fdi || fdi.length !== 2) return null;
  const q = fdi.charAt(0);
  if (q === '1' || q === '5') return 'Q1';
  if (q === '2' || q === '6') return 'Q2';
  if (q === '3' || q === '7') return 'Q3';
  if (q === '4' || q === '8') return 'Q4';
  return null;
}

export function isValidFdiToken(token) {
  if (!token) return false;
  return (
    PERMANENT_RE.test(token)
    || PRIMARY_RE.test(token)
    || QUADRANT_RE.test(token)
    || FULL_MOUTH_RE.test(token)
  );
}

/**
 * Apply mutual-exclusion rules. Returns the next array.
 */
export function applyAddToken(currentTokens, candidate) {
  const cur = currentTokens || [];
  if (!isValidFdiToken(candidate)) {
    throw new Error(`Invalid FDI token: ${candidate}`);
  }
  // Already present → noop.
  if (cur.includes(candidate)) return cur;

  // FM displaces everything.
  if (FULL_MOUTH_RE.test(candidate)) return ['FM'];

  // Any other addition drops FM first.
  let next = cur.filter((t) => t !== 'FM');

  if (QUADRANT_RE.test(candidate)) {
    // Drop any FDI numbers that fall within this quadrant + any
    // existing same quadrant entry (idempotent).
    const covered = new Set(QUADRANT_FDI_MAP[candidate] || []);
    next = next.filter((t) => !covered.has(t) && t !== candidate);
    next.push(candidate);
    return next;
  }

  // Permanent or primary FDI candidate.
  // Reject if its quadrant is already on the list (Q1 covers 16).
  const q = quadrantOfFdi(candidate);
  if (q && next.includes(q)) {
    throw new Error(`Quadrant ${q} already covers tooth ${candidate}`);
  }
  next.push(candidate);
  return next;
}

export function applyRemoveToken(currentTokens, token) {
  return (currentTokens || []).filter((t) => t !== token);
}

const QUADRANT_LABELS = {
  Q1: 'Q1',
  Q2: 'Q2',
  Q3: 'Q3',
  Q4: 'Q4',
  FM: 'FM',
};

export default function ToothNumberInput({
  value = [],
  onChange,
  dentitionMode = 'adult',
  isDark = false,
  inputId,
  disabled = false,
}) {
  const [draft, setDraft] = useState('');
  const [errorMsg, setErrorMsg] = useState(null);

  const commitToken = useCallback((raw) => {
    const cleaned = (raw || '').trim().toUpperCase();
    if (!cleaned) return;
    if (!isValidFdiToken(cleaned)) {
      setErrorMsg(`"${raw}" is not a valid FDI token`);
      return;
    }
    try {
      const next = applyAddToken(value, cleaned);
      onChange?.(next);
      setErrorMsg(null);
    } catch (e) {
      setErrorMsg(e.message);
    }
  }, [value, onChange]);

  const onKey = (e) => {
    if (e.key === ',' || e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      if (draft.trim()) {
        commitToken(draft);
        setDraft('');
      }
    } else if (e.key === 'Backspace' && draft === '' && (value || []).length) {
      // Remove last chip.
      onChange?.((value || []).slice(0, -1));
    }
  };

  const onChangeRaw = (e) => {
    const v = e.target.value;
    // Accept comma/space separated bulk paste in one go.
    if (v.includes(',') || v.includes(' ')) {
      const parts = v.split(/[,\s]+/).filter(Boolean);
      let acc = value || [];
      let firstErr = null;
      for (const p of parts) {
        try {
          acc = applyAddToken(acc, p.toUpperCase());
        } catch (err) {
          if (!firstErr) firstErr = err.message;
        }
      }
      onChange?.(acc);
      setDraft('');
      setErrorMsg(firstErr);
      return;
    }
    setDraft(v);
    setErrorMsg(null);
  };

  const tinted = isDark
    ? 'bg-slate-800 text-slate-100 border-slate-600'
    : 'bg-white text-slate-900 border-slate-300';
  const chipTint = isDark
    ? 'bg-indigo-900/40 border-indigo-700 text-indigo-100'
    : 'bg-indigo-100 border-indigo-400 text-indigo-900';
  const quadButtonTint = isDark
    ? 'bg-slate-800 text-slate-100 border-slate-600 hover:bg-slate-700'
    : 'bg-white text-slate-800 border-slate-300 hover:bg-slate-50';

  return (
    <div>
      <div
        className={[
          'flex flex-wrap items-center gap-1.5 px-2 py-2 rounded-md border min-h-[40px]',
          tinted,
          disabled ? 'opacity-60' : '',
        ].join(' ')}
      >
        {(value || []).map((t) => (
          <span
            key={t}
            className={[
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium',
              chipTint,
            ].join(' ')}
          >
            {t}
            {!disabled && (
              <button
                type="button"
                onClick={() => onChange?.(applyRemoveToken(value, t))}
                className="ml-0.5 leading-none text-current/70 hover:text-current"
                aria-label={`Remove ${t}`}
              >
                ×
              </button>
            )}
          </span>
        ))}
        <input
          id={inputId}
          type="text"
          value={draft}
          onChange={onChangeRaw}
          onKeyDown={onKey}
          onBlur={() => {
            if (draft.trim()) {
              commitToken(draft);
              setDraft('');
            }
          }}
          disabled={disabled}
          className="flex-1 min-w-[6rem] bg-transparent outline-none text-sm placeholder:text-slate-400"
          placeholder={
            (value || []).length === 0
              ? 'e.g. 16, 26, 47 — or Q1 / FM'
              : 'add more…'
          }
        />
      </div>

      {/* Quadrant / full-mouth shortcut strip */}
      <div className="mt-1.5 flex items-center gap-1 flex-wrap">
        {['Q1', 'Q2', 'Q3', 'Q4', 'FM'].map((q) => {
          const active = (value || []).includes(q);
          return (
            <button
              key={q}
              type="button"
              disabled={disabled}
              onClick={() => {
                try {
                  const next = active
                    ? applyRemoveToken(value, q)
                    : applyAddToken(value, q);
                  onChange?.(next);
                  setErrorMsg(null);
                } catch (err) {
                  setErrorMsg(err.message);
                }
              }}
              className={[
                'px-2 py-0.5 text-xs font-medium rounded-full border',
                active
                  ? 'bg-orange-600 text-white border-orange-600'
                  : quadButtonTint,
              ].join(' ')}
              aria-pressed={active}
            >
              {QUADRANT_LABELS[q]}
            </button>
          );
        })}
        {dentitionMode && (
          <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            mode: {dentitionMode}
          </span>
        )}
      </div>

      {errorMsg && (
        <div className="mt-1 text-xs text-rose-600 dark:text-rose-300">
          {errorMsg}
        </div>
      )}
    </div>
  );
}
